import { Field, InputNumber, Switch, RadioGroup, Checkbox, LoaderIcon, Select, Radio, Input } from '@repo/go-ui';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SPLIT_RANGE_DEPTH, SPLIT_RANGE_TIME_IN_SECONDS } from 'src/api/consts';

import type { Stack } from '../../../../packages/data/structures/Stack';
import type { BaseSettingsScreen } from '../screen/BaseSettingsScreen';

import type { OthersEntity } from './Others.entity';
import styles from './Others.module.scss';
import { OthersScreenStore } from './OthersScreen.store';

// TODO: костыль из-за странного поведения InputNumber
// InputNumber не корректно отрабатывает с дефолтным значением.
const defaultNumberParser = (value: string | number) => {
  const numberMatch = value
    .toString()
    .trim()
    .match(/-?[0-9]*\.?[0-9]*(e[0-9]*)?/);

  const number = numberMatch?.at(0);
  const index = Number.parseInt(numberMatch?.index?.toString() ?? '');

  return {
    number: number ?? value,
    endPosition: index !== undefined && !Number.isNaN(index) ? index + (numberMatch?.at(0)?.length ?? 0) : null,
  };
};

type Props = {
  stack: Stack<BaseSettingsScreen>;
  screen: OthersEntity;
};

export const OthersScreen = observer(function OthersScreen({ screen, stack }: Props) {
  const [store] = useState(() => new OthersScreenStore(stack, screen));
  const [defaultSplitRangeTime, setDefaultSplitRangeTime] = useState<string>(store.defaultSplitRangeTime.toString());
  const [defaultSplitRangeDepth, setDefaultSplitRangeDepth] = useState<string>(store.defaultSplitRangeDepth.toString());
  const { t } = useTranslation();

  const renderLocationConfigRow = (
    locationType: 'block' | 'field' | 'pad',
    labelKey: string,
    hasPrefix: boolean = true,
  ) => {
    const config = store.locationNameConfigurator[locationType];

    return (
      <tr className={styles.locationConfigRow}>
        <td>{t(labelKey)}</td>
        <td>
          <InputNumber
            value={config.order}
            onChange={(value) => {
              const numberValue = Number(value);
              if (value !== null && !Number.isNaN(numberValue)) {
                store.setLocationOrder(locationType, numberValue);
              } else {
                store.setLocationOrder(locationType, null);
              }
            }}
            min={1}
            max={3}
            style={{ width: 80 }}
          />
        </td>
        <td style={{ width: 95 }}>
          <Radio
            checked={config.displayName === 'name'}
            value="name"
            onChange={(value) => store.setLocationDisplayName(locationType, value)}
          />
        </td>
        <td style={{ width: 95 }}>
          <Radio
            checked={config.displayName === 'abbreviatedName'}
            value="abbreviatedName"
            onChange={(value) => store.setLocationDisplayName(locationType, value)}
          />
        </td>
        <td style={{ width: 95 }}>
          {hasPrefix ? (
            <Radio
              checked={config.displayName === 'prefix'}
              value="prefix"
              onChange={(value) => store.setLocationDisplayName(locationType, value)}
            />
          ) : null}
        </td>
      </tr>
    );
  };

  return (
    <article className={styles.column}>
      <div className={styles.contentWrapper}>
        <div className={styles.block}>
          <div className={styles.blockTitle}>{t('settings:Others:general')}</div>
          <div className={styles.subBlock}>
            <div className={styles.settingsOption}>
              <Switch
                size="s"
                checked={store.isAltNames}
                onChange={(value: boolean) => {
                  store.setIsAltNamesOn(value);
                }}
              />
              <span>{t('settings:Others:publicNames')}</span>
            </div>
            <div className={styles.settingsOption}>
              <Switch
                size="s"
                checked={store.defaultDepthUnits === 'INDEX_DEPTH_SCALE_FEET'}
                onChange={(value: boolean) => {
                  store.setDefaultDepthUnits(value ? 'INDEX_DEPTH_SCALE_FEET' : 'INDEX_DEPTH_SCALE_METERS');
                }}
              />
              <span>{t('settings:Others:defaultDepthUnits')}</span>
            </div>
          </div>
          <div className={styles.subBlock}>
            <div className={styles.title} style={{ marginBottom: 8 }}>
              {t('settings:Others:widgetHeaderFontSize')}
            </div>
            <div style={{ display: 'flex' }}>
              <Field hint={t('settings:Others:widgetHeaderFontSizeHint')} className={styles.settingsField}>
                <InputNumber
                  size="s"
                  value={store.widgetHeaderFontSize}
                  min={13}
                  max={40}
                  suffix="рх"
                  onChange={(value) => {
                    if (!Number.isNaN(value)) {
                      store.setWidgetHeaderFontSize(Number(value));
                    }
                  }}
                />
              </Field>
              <div style={{ marginLeft: 24 }}>
                <span style={{ color: 'var(--widget-header-title)' }}>{t('settings:Others:example')} </span>
                <span style={{ fontSize: Math.min(40, store.widgetHeaderFontSize) }}>
                  VT_535, Main
                  <span style={{ color: 'var(--widget-header-title)' }}>
                    : {`${t('widgets:WellLogs.title')} (${t('widgets:time')})`}
                  </span>
                </span>
              </div>
            </div>
          </div>

          <div className={styles.subBlock}>
            <div className={styles.title} style={{ marginBottom: 8 }}>
              {t('settings:Others:autoSelectWellbore')}
            </div>
            <div className={styles.settingsOption}>
              <Checkbox
                size="s"
                checked={store.autoSelectWellboreForTime === 'active'}
                onChange={(value: boolean) => {
                  store.setAutoSelectWellbore(value ? 'active' : 'main');
                }}
              >
                {t('settings:Others:autoSelectWellboreForTime')}
              </Checkbox>
            </div>
            <div className={styles.settingsOption}>
              <Checkbox
                size="s"
                checked={store.autoSelectWellboreForDepth === 'active'}
                onChange={(value: boolean) => {
                  store.setAutoSelectWellboreForDepth(value ? 'active' : 'main');
                }}
              >
                {t('settings:Others:autoSelectWellboreForDepth')}
              </Checkbox>
            </div>
          </div>
        </div>
        <div className={styles.block}>
          <div className={styles.blockTitle}>{t('settings:Others:trajectory')}</div>
          <div className={styles.trajectoryRow}>
            <span className={styles.trajectoryLabel}>{t('settings:Others:azimuthReference')}</span>
            <RadioGroup
              style={{ display: 'flex', gap: 24 }}
              value={store.azimuthReference}
              items={[
                { value: 'MAGNETIC', label: t('settings:Others:azimuthReferenceMagnetic') },
                { value: 'TRUE', label: t('settings:Others:azimuthReferenceTrue') },
                { value: 'GRID', label: t('settings:Others:azimuthReferenceGrid') },
              ]}
              onChange={store.setAzimuthReference}
            />
          </div>
          <div className={styles.trajectoryRow}>
            <span className={styles.trajectoryLabel}>{t('settings:Others:systemDatum')}</span>
            <Select
              className={styles.trajectorySelect}
              size="s"
              allowClear
              value={store.systemDatumId}
              options={store.systemDatumSelectOptions}
              onChange={(value) => {
                store.setSystemDatumId(typeof value === 'number' ? value : null);
              }}
            />
          </div>
        </div>
        <div className={styles.block}>
          <div className={styles.blockTitle}>{t('settings:Others:wellListTitle')}</div>
          <div className={styles.subBlock}>
            <div className={styles.title}>{t('settings:Others:defaultParameters')}</div>
            <div style={{ display: 'flex' }}>
              <Field label={t('settings:Others:filter')} style={{ width: 220 }}>
                <Select
                  size="s"
                  value={store.defaultWellListFilter}
                  onChange={(value: 'active' | 'favorites' | 'all') => {
                    store.setDefaultWellListFilter(value);
                  }}
                  options={[
                    { value: 'active', label: t('settings:Others:active') },
                    { value: 'favorites', label: t('settings:Others:favorites') },
                    { value: 'all', label: t('settings:Others:all') },
                  ]}
                />
              </Field>
              <Field label={t('settings:Others:group')} style={{ marginLeft: 24, width: 220 }}>
                <Select
                  size="s"
                  value={store.defaultWellListGroup}
                  onChange={(
                    value:
                      | 'wellsGroupingStatus'
                      | 'wellsGroupingDrillingRig'
                      | 'wellsGroupingRigCrew'
                      | 'wellsGroupingLocation',
                  ) => {
                    store.setDefaultWellListGroup(value);
                  }}
                  options={[
                    { value: 'wellsGroupingStatus', label: t('settings:Others:wellsGroupingStatus') },
                    { value: 'wellsGroupingDrillingRig', label: t('settings:Others:wellsGroupingDrillingRig') },
                    { value: 'wellsGroupingRigCrew', label: t('settings:Others:wellsGroupingRigCrew') },
                    { value: 'wellsGroupingLocation', label: t('settings:Others:wellsGroupingLocation') },
                  ]}
                />
              </Field>
            </div>
          </div>
        </div>
        <div className={styles.block}>
          <div className={styles.blockTitle}>{t('settings:Others:locationNameConfigurator')}</div>
          <div className={styles.subBlock}>
            <table className={styles.locationConfigTable}>
              <thead>
                <tr>
                  <th style={{ width: 202 }}></th>
                  <th style={{ width: 100 }}>{t('settings:Others:order')}</th>
                  <th colSpan={3} style={{ width: 285 }}>
                    {t('settings:Others:displayName')}
                  </th>
                </tr>
                <tr>
                  <th></th>
                  <th></th>
                  <th className={styles.subTitle}>{t('settings:Others:name')}</th>
                  <th className={styles.subTitle}>{t('settings:Others:abbreviatedName')}</th>
                  <th className={styles.subTitle}>{t('settings:Others:prefix')}</th>
                </tr>
              </thead>
              <tbody>
                {renderLocationConfigRow('block', 'settings:Others:blockName')}
                {renderLocationConfigRow('field', 'settings:Others:fieldName')}
                {renderLocationConfigRow('pad', 'settings:Others:padName', false)}
              </tbody>
            </table>
          </div>
        </div>
        <div className={styles.block}>
          <div className={styles.blockTitle}>{t('settings:Others:wellLogsWidgetTitle')}</div>
          <div className={styles.subBlock}>
            <div className={styles.settingsOption}>
              <Switch
                size="s"
                checked={store.isColoredParamsHeaders}
                onChange={(value: boolean) => {
                  store.setIsColoredParamsHeaders(value);
                }}
              />
              <span>{t('settings:Others:coloredParametersHeaders')}</span>
            </div>
          </div>
          <div className={styles.subBlock}>
            <div className={styles.title}>{t('settings:Others:defaultSplitRange')}</div>
            <div style={{ display: 'flex', gap: 24 }}>
              <Field label={t('settings:Others:time')}>
                <Input
                  size="s"
                  value={defaultSplitRangeTime}
                  allowClear={false}
                  onChange={(e) => {
                    const { number } = defaultNumberParser(e.target.value);
                    setDefaultSplitRangeTime(number.toString());
                  }}
                  onBlur={(event) => {
                    const value = event.target.value;
                    let transformedValue: number;

                    if (value === '' || value === '-' || Number.isNaN(Number(value)) || Number(value) < 1) {
                      transformedValue = Number(SPLIT_RANGE_TIME_IN_SECONDS);
                    } else {
                      transformedValue = Number(value);
                    }

                    setDefaultSplitRangeTime(transformedValue.toString());
                    store.setDefaultSplitRangeTime(transformedValue);
                  }}
                  suffix="s"
                />
              </Field>

              <Field label={t('settings:Others:depth')}>
                <Input
                  size="s"
                  value={defaultSplitRangeDepth}
                  allowClear={false}
                  onChange={(e) => {
                    const { number } = defaultNumberParser(e.target.value);
                    setDefaultSplitRangeDepth(number.toString());
                  }}
                  onBlur={(event) => {
                    const value = event.target.value;
                    let transformedValue: number;

                    if (value === '' || value === '-' || Number.isNaN(Number(value)) || Number(value) < 1) {
                      transformedValue = Number(SPLIT_RANGE_DEPTH);
                    } else {
                      transformedValue = Number(value);
                    }

                    setDefaultSplitRangeDepth(transformedValue.toString());
                    store.setDefaultSplitRangeDepth(transformedValue);
                  }}
                  suffix={store.defaultDepthUnits === 'INDEX_DEPTH_SCALE_METERS' ? 'm' : 'ft'}
                />
              </Field>
            </div>
          </div>
          <div className={styles.subBlock}>
            <div className={styles.title}>{t('settings:Others:applicationPerformance')}</div>
            <div className={styles.blockHint}>{t('settings:Others:applicationPerformanceHint')}</div>
            <div style={{ marginTop: 16 }}>
              <RadioGroup
                style={{ display: 'flex', gap: 56 }}
                value={store.graphicsPerformance}
                items={[
                  { value: 'ultra', label: t('settings:Others:ultra') },
                  { value: 'high', label: t('settings:Others:high') },
                  { value: 'medium', label: t('settings:Others:medium') },
                  { value: 'low', label: t('settings:Others:low') },
                ]}
                onChange={store.setGraphicsPerformance}
              />
            </div>
          </div>
        </div>
      </div>
      {store.hasAsyncLoading && (
        <div className={styles.loading}>
          <LoaderIcon className={styles.loadingIcon} />
        </div>
      )}
    </article>
  );
});
